import { useEffect, useMemo, useRef, useState } from 'react'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import ArchiveRoundedIcon from '@mui/icons-material/ArchiveRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded'
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded'
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded'
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import UnpublishedRoundedIcon from '@mui/icons-material/UnpublishedRounded'
import { Button, Toggle } from '../../Common'
import { CATEGORY_ICONS, PreviewRenderer } from '../../Explore/TemplatePreviewRenderer'
import { AutomationPayloadEditor } from './templateEditors/AutomationPayloadEditor'
import { normalizeAutomationPayload, type AutomationPayload } from './templateEditors/automationPayload'
import { ChecklistPayloadEditor } from './templateEditors/ChecklistPayloadEditor'
import { normalizeChecklistPayload, type ChecklistPayload } from './templateEditors/checklistPayload'
import { DesignPayloadEditor } from './templateEditors/DesignPayloadEditor'
import { normalizeDesignPayload, type DesignPayload } from './templateEditors/designPayload'
import { FormPayloadEditor } from './templateEditors/FormPayloadEditor'
import { normalizeFormPayload, type FormPayload } from './templateEditors/formPayload'
import {
  EXPLORE_TEMPLATES_REGISTRY_COLLECTION,
  computeTemplatePreviewDataHash,
  deleteOwnerExploreTemplate,
  saveOwnerExploreTemplate,
  subscribeOwnerTemplateRegistry,
  updateOwnerExploreTemplateStatus,
} from '../../../services/exploreRegistryService'
import { BUNDLED_EXPLORE_TEMPLATES } from '../../../data/explore_templates'
import {
  TEMPLATE_CATEGORIES,
  type ExploreTemplateDoc,
  type ExploreTemplateStatus,
  type TemplateCategory,
  type TemplatePreviewData,
  type TemplateRegistryEntry,
  type TemplateTier,
} from '../../../types/templates'

type OwnerTemplatesPanelProps = {
  actorUid: string
}

type TemplateProvenance = 'bundled' | 'firestore' | 'overriding'
type AdminFilter = 'all' | 'drafts' | 'archived' | 'bundled-only' | 'firestore-only'
type EditorContentMode = 'content' | 'rawPreview' | 'rawPayload'

type OwnerTemplateRow = {
  id: string
  template: TemplateRegistryEntry
  firestore: ExploreTemplateDoc | null
  bundled: TemplateRegistryEntry | null
  provenance: TemplateProvenance
}

type EditorState = ExploreTemplateDoc & {
  previewJson: string
  payloadJson: string
}

type ImportPreviewState = {
  entries: TemplateRegistryEntry[]
  adds: number
  overrides: number
  noops: number
}

const MANAGED_CATEGORIES = TEMPLATE_CATEGORIES.filter((category) => category !== 'Workspaces') as Exclude<TemplateCategory, 'Workspaces'>[]

const DEFAULT_PREVIEW = (title = 'Untitled template'): TemplatePreviewData => ({
  title,
  summary: '',
  metrics: [],
  sections: [],
  payload: {},
})

const slugifyTemplateId = (value: string) => value
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 80)

const toDateLabel = (value: unknown) => {
  if (!value) return 'None'
  const date = value instanceof Date
    ? value
    : typeof (value as { toDate?: () => Date }).toDate === 'function'
      ? (value as { toDate: () => Date }).toDate()
      : new Date(String(value))
  if (Number.isNaN(date.getTime())) return 'None'
  return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

const asDoc = (
  template: TemplateRegistryEntry,
  actorUid: string,
  status: ExploreTemplateStatus,
  overridesBundledId: string | null,
): ExploreTemplateDoc => ({
  ...template,
  status,
  overridesBundledId,
  createdByUid: actorUid,
  updatedByUid: actorUid,
  publishedAt: status === 'PUBLISHED' ? new Date() : null,
  previewDataHash: computeTemplatePreviewDataHash(template.previewData),
})

const createBlankDoc = (category: TemplateCategory, actorUid: string): ExploreTemplateDoc => {
  const id = `template-${category.toLowerCase()}-${Date.now().toString(36)}`
  return asDoc({
    id,
    title: 'Untitled template',
    description: '',
    category,
    tags: [],
    registryVersion: 1,
    previewData: DEFAULT_PREVIEW(),
    createdAt: null,
    updatedAt: null,
    tier: 'FREE',
    featured: false,
  }, actorUid, 'DRAFT', null)
}

const toEditorState = (doc: ExploreTemplateDoc): EditorState => ({
  ...doc,
  previewJson: JSON.stringify(doc.previewData, null, 2),
  payloadJson: JSON.stringify(doc.previewData.payload ?? {}, null, 2),
})

const parseList = (value: string) => value.split(',').map((item) => item.trim()).filter(Boolean)
const hasStructuredEditor = (category: TemplateCategory) => category === 'Checklists' || category === 'Automations' || category === 'Forms' || category === 'Designs'
const defaultEditorContentMode = (category: TemplateCategory): EditorContentMode => hasStructuredEditor(category) ? 'content' : 'rawPayload'
const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
const parseEditorPayload = (value: string): Record<string, unknown> | null => {
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, unknown> : null
  } catch {
    return null
  }
}

const templateEntryForJson = (template: TemplateRegistryEntry): TemplateRegistryEntry => ({
  id: template.id,
  title: template.title,
  description: template.description,
  category: template.category,
  tags: [...template.tags],
  registryVersion: template.registryVersion,
  previewData: {
    title: template.previewData.title,
    summary: template.previewData.summary,
    metrics: template.previewData.metrics ?? [],
    sections: template.previewData.sections ?? [],
    payload: template.previewData.payload ?? {},
  },
  createdAt: null,
  updatedAt: null,
  featured: template.featured === true,
  tier: template.tier ?? 'FREE',
})

const validateTemplateRegistryEntry = (value: unknown): TemplateRegistryEntry => {
  if (!isRecord(value)) throw new Error('Each import entry must be an object.')
  const category = value.category as TemplateCategory
  if (!MANAGED_CATEGORIES.includes(category as never)) throw new Error(`Unsupported template category: ${String(value.category ?? '') || 'blank'}.`)
  if (typeof value.id !== 'string' || !value.id.trim()) throw new Error('Every entry needs a non-empty id.')
  if (typeof value.title !== 'string' || !value.title.trim()) throw new Error(`Entry ${value.id} needs a non-empty title.`)
  if (typeof value.description !== 'string') throw new Error(`Entry ${value.id} needs a description string.`)
  if (!Array.isArray(value.tags)) throw new Error(`Entry ${value.id} needs a tags array.`)
  const registryVersion = typeof value.registryVersion === 'number' && Number.isInteger(value.registryVersion) ? value.registryVersion : null
  if (registryVersion === null || registryVersion < 1) throw new Error(`Entry ${value.id} needs registryVersion >= 1.`)
  if (!isRecord(value.previewData)) throw new Error(`Entry ${value.id} needs previewData.`)
  if (typeof value.previewData.title !== 'string') throw new Error(`Entry ${value.id} previewData.title must be a string.`)
  if (typeof value.previewData.summary !== 'string') throw new Error(`Entry ${value.id} previewData.summary must be a string.`)
  if (value.previewData.payload !== undefined && !isRecord(value.previewData.payload)) throw new Error(`Entry ${value.id} previewData.payload must be an object.`)
  return templateEntryForJson({
    id: value.id.trim(),
    title: value.title.trim(),
    description: value.description,
    category,
    tags: value.tags.map(String).map((tag) => tag.trim()).filter(Boolean),
    registryVersion,
    previewData: {
      title: value.previewData.title,
      summary: value.previewData.summary,
      metrics: Array.isArray(value.previewData.metrics) ? value.previewData.metrics as TemplatePreviewData['metrics'] : [],
      sections: Array.isArray(value.previewData.sections) ? value.previewData.sections as TemplatePreviewData['sections'] : [],
      payload: isRecord(value.previewData.payload) ? value.previewData.payload : {},
    },
    createdAt: null,
    updatedAt: null,
    featured: value.featured === true,
    tier: value.tier === 'PREMIUM' ? 'PREMIUM' : 'FREE',
  })
}

const parseImportEntries = (raw: string): TemplateRegistryEntry[] => {
  const parsed = JSON.parse(raw) as unknown
  if (!Array.isArray(parsed)) throw new Error('Import JSON must be a TemplateRegistryEntry[] array.')
  return parsed.map(validateTemplateRegistryEntry)
}

const downloadJson = (filename: string, value: unknown) => {
  const blob = new Blob([`${JSON.stringify(value, null, 2)}\n`], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

const rowsFromSources = (firestoreTemplates: ExploreTemplateDoc[]): OwnerTemplateRow[] => {
  const bundledById = new Map(BUNDLED_EXPLORE_TEMPLATES.filter((template) => template.category !== 'Workspaces').map((template) => [template.id, template]))
  const rows = new Map<string, OwnerTemplateRow>()
  for (const bundled of bundledById.values()) {
    rows.set(bundled.id, {
      id: bundled.id,
      template: bundled,
      firestore: null,
      bundled,
      provenance: 'bundled',
    })
  }
  for (const firestore of firestoreTemplates) {
    const bundled = bundledById.get(firestore.id) ?? null
    rows.set(firestore.id, {
      id: firestore.id,
      template: firestore,
      firestore,
      bundled,
      provenance: bundled ? 'overriding' : 'firestore',
    })
  }
  return Array.from(rows.values()).sort((left, right) => {
    const categoryDelta = MANAGED_CATEGORIES.indexOf(left.template.category as never) - MANAGED_CATEGORIES.indexOf(right.template.category as never)
    if (categoryDelta !== 0) return categoryDelta
    const statusDelta = (right.firestore?.status === 'PUBLISHED' ? 1 : 0) - (left.firestore?.status === 'PUBLISHED' ? 1 : 0)
    if (statusDelta !== 0) return statusDelta
    return left.template.title.localeCompare(right.template.title)
  })
}

export const OwnerTemplatesPanel = ({ actorUid }: OwnerTemplatesPanelProps) => {
  const [firestoreTemplates, setFirestoreTemplates] = useState<ExploreTemplateDoc[]>([])
  const [category, setCategory] = useState<TemplateCategory | 'all'>('all')
  const [filter, setFilter] = useState<AdminFilter>('all')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [editor, setEditor] = useState<EditorState | null>(null)
  const [editorContentMode, setEditorContentMode] = useState<EditorContentMode>('rawPayload')
  const [editorPaneMode, setEditorPaneMode] = useState<'fields' | 'preview'>('preview')
  const [importJson, setImportJson] = useState('')
  const [importPreview, setImportPreview] = useState<ImportPreviewState | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  const [message, setMessage] = useState<{ tone: 'good' | 'warn' | 'danger'; text: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const editorShellRef = useRef<HTMLDivElement | null>(null)
  const saveEditorRef = useRef<() => Promise<void>>(async () => undefined)

  useEffect(() => subscribeOwnerTemplateRegistry(setFirestoreTemplates, (error) => {
    setMessage({ tone: 'danger', text: error instanceof Error ? error.message : 'Templates could not be loaded.' })
  }), [])

  const rows = useMemo(() => rowsFromSources(firestoreTemplates), [firestoreTemplates])
  const visibleRows = useMemo(() => rows.filter((row) => {
    if (category !== 'all' && row.template.category !== category) return false
    if (filter === 'drafts') return row.firestore?.status === 'DRAFT'
    if (filter === 'archived') return row.firestore?.status === 'ARCHIVED'
    if (filter === 'bundled-only') return !row.firestore && Boolean(row.bundled)
    if (filter === 'firestore-only') return Boolean(row.firestore) && !row.bundled
    return true
  }), [category, filter, rows])

  const [prevVisibleRows, setPrevVisibleRows] = useState(visibleRows)
  if (prevVisibleRows !== visibleRows) {
    setPrevVisibleRows(visibleRows)
    if (!selectedId || !visibleRows.some((row) => row.id === selectedId)) {
      setSelectedId(visibleRows[0]?.id ?? null)
    }
  }

  const selected = visibleRows.find((row) => row.id === selectedId) ?? null
  const countsByCategory = useMemo(() => {
    const counts = new Map<TemplateCategory, number>()
    for (const row of rows) counts.set(row.template.category, (counts.get(row.template.category) ?? 0) + 1)
    return counts
  }, [rows])
  const statusCounts = useMemo(() => ({
    drafts: rows.filter((row) => row.firestore?.status === 'DRAFT').length,
    archived: rows.filter((row) => row.firestore?.status === 'ARCHIVED').length,
    bundledOnly: rows.filter((row) => !row.firestore && row.bundled).length,
    firestoreOnly: rows.filter((row) => row.firestore && !row.bundled).length,
  }), [rows])
  const firestoreById = useMemo(() => new Map(firestoreTemplates.map((template) => [template.id, template])), [firestoreTemplates])
  const exportRows = category === 'all' ? rows : rows.filter((row) => row.template.category === category)

  const run = async (action: () => Promise<void>, success: string) => {
    setBusy(true)
    setMessage(null)
    try {
      await action()
      setMessage({ tone: 'good', text: success })
    } catch (error) {
      setMessage({ tone: 'danger', text: error instanceof Error ? error.message : 'Template operation failed.' })
    } finally {
      setBusy(false)
    }
  }

  const openEditor = (doc: ExploreTemplateDoc) => {
    setEditor(toEditorState(doc))
    setEditorContentMode(defaultEditorContentMode(doc.category))
    setEditorPaneMode('preview')
  }

  const exportTemplate = (template: TemplateRegistryEntry) => {
    downloadJson(`${template.id}.json`, [templateEntryForJson(template)])
  }

  const exportCurrentCategory = () => {
    const scope = category === 'all' ? 'all' : category.toLowerCase()
    downloadJson(`explore-templates-${scope}.json`, exportRows.map((row) => templateEntryForJson(row.template)))
  }

  const previewImport = () => {
    try {
      const entries = parseImportEntries(importJson)
      let adds = 0
      let overrides = 0
      let noops = 0
      for (const entry of entries) {
        const existing = firestoreById.get(entry.id)
        if (!existing) adds += 1
        else if (JSON.stringify(templateEntryForJson(existing)) === JSON.stringify(entry)) noops += 1
        else overrides += 1
      }
      setImportPreview({ entries, adds, overrides, noops })
      setMessage(null)
    } catch (error) {
      setImportPreview(null)
      setMessage({ tone: 'danger', text: error instanceof Error ? error.message : 'Import JSON is not valid.' })
    }
  }

  const confirmImport = async () => {
    if (!importPreview) return
    await run(async () => {
      const writes = importPreview.entries.filter((entry) => {
        const existing = firestoreById.get(entry.id)
        return !existing || JSON.stringify(templateEntryForJson(existing)) !== JSON.stringify(entry)
      })
      await Promise.all(writes.map((entry) => saveOwnerExploreTemplate(
        asDoc(entry, actorUid, 'DRAFT', BUNDLED_EXPLORE_TEMPLATES.some((bundled) => bundled.id === entry.id) ? entry.id : null),
        actorUid,
      )))
      setImportOpen(false)
      setImportPreview(null)
      setImportJson('')
    }, `Imported ${importPreview.adds + importPreview.overrides} template${importPreview.adds + importPreview.overrides === 1 ? '' : 's'}.`)
  }

  const updateChecklistPayload = (next: ChecklistPayload) => {
    setEditor((current) => current ? {
      ...current,
      payloadJson: JSON.stringify(normalizeChecklistPayload(next), null, 2),
    } : current)
  }

  const updateAutomationPayload = (next: AutomationPayload) => {
    setEditor((current) => current ? {
      ...current,
      payloadJson: JSON.stringify(normalizeAutomationPayload(next), null, 2),
    } : current)
  }

  const updateFormPayload = (next: FormPayload) => {
    setEditor((current) => current ? {
      ...current,
      payloadJson: JSON.stringify(normalizeFormPayload(next), null, 2),
    } : current)
  }

  const updateDesignPayload = (next: DesignPayload) => {
    setEditor((current) => current ? {
      ...current,
      payloadJson: JSON.stringify(normalizeDesignPayload(next), null, 2),
    } : current)
  }

  const saveEditor = async () => {
    if (!editor) return
    let previewData: TemplatePreviewData
    try {
      previewData = JSON.parse(editor.previewJson) as TemplatePreviewData
    } catch {
      setMessage({ tone: 'danger', text: 'Preview JSON is not valid.' })
      return
    }
    let payload: Record<string, unknown>
    try {
      payload = JSON.parse(editor.payloadJson) as Record<string, unknown>
    } catch {
      setMessage({ tone: 'danger', text: 'Payload JSON is not valid.' })
      return
    }
    const title = editor.title.trim() || editor.previewData.title || 'Untitled template'
    const id = slugifyTemplateId(editor.id) || slugifyTemplateId(title)
    await run(async () => {
      await saveOwnerExploreTemplate({
        ...editor,
        id,
        title,
        description: editor.description.trim(),
        tags: editor.tags,
        previewData: {
          ...previewData,
          title: previewData.title || title,
          payload,
        },
      }, actorUid)
      setEditor(null)
      setSelectedId(id)
    }, 'Template saved.')
  }

  useEffect(() => {
    saveEditorRef.current = saveEditor
  })

  useEffect(() => {
    if (!editor) return
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const frame = window.requestAnimationFrame(() => {
      const shell = editorShellRef.current
      shell?.querySelector<HTMLElement>('button, input, select, textarea, [tabindex]:not([tabindex="-1"])')?.focus()
    })
    return () => {
      window.cancelAnimationFrame(frame)
      previousFocus?.focus()
    }
  }, [editor])

  useEffect(() => {
    if (!editor) return
    const focusableSelector = 'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setEditor(null)
        return
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
        event.preventDefault()
        if (!busy) void saveEditorRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const shell = editorShellRef.current
      if (!shell) return
      const focusable = Array.from(shell.querySelectorAll<HTMLElement>(focusableSelector))
        .filter((node) => node.offsetParent !== null || node === document.activeElement)
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [busy, editor])

  const [resettingDemo, setResettingDemo] = useState(false)

  const handleResetDemo = async () => {
    setResettingDemo(true)
    try {
      const { firestoreDb } = await import('../../../services/firebase')
      const { resetIntegrationsDemoWorkspace } = await import('../../../explore_seeds/integrationsDemoSeed')
      const res = await resetIntegrationsDemoWorkspace(firestoreDb)
      const msg = `Integrations Demo reset to canonical seed. Scrubbed: ${res.scrubbedDebris.syncJobs} jobs, ${res.scrubbedDebris.inboundEvents} events, ${res.scrubbedDebris.watchChannels} channels, ${res.scrubbedDebris.mirrors} mirrors.`
      setMessage({ text: msg, tone: 'good' })
    } catch (err) {
      const errText = `Reset failed: ${err instanceof Error ? err.message : String(err)}`
      setMessage({ text: errText, tone: 'danger' })
    } finally {
      setResettingDemo(false)
    }
  }

  const selectedDoc = selected?.firestore ?? null
  const parsedEditorPayload = editor ? parseEditorPayload(editor.payloadJson) : null

  return (
    <section className="ownerPanel ownerTemplatesPanel" aria-label="Owner template registry">
      <div className="ownerPanelHeader">
        <div>
          <span className="ownerEyebrow">Explore templates</span>
          <h2>Template registry management</h2>
          <p>Manage Firestore overrides for Designs, Automations, Forms, and Checklists. Bundled JSON remains read-only fallback.</p>
        </div>
        <div className="ownerSettingsActions">
          <Button variant="secondary" size="sm" icon={<FileUploadRoundedIcon fontSize="inherit" />} onClick={() => setImportOpen(true)}>Import JSON</Button>
          <Button variant="primary" size="sm" icon={<AddRoundedIcon fontSize="inherit" />} onClick={() => openEditor(createBlankDoc(category === 'all' ? 'Designs' : category, actorUid))}>New template</Button>
        </div>
      </div>

      {message ? (
        <div className={`ownerActionResult ownerTone--${message.tone}`} role="status">
          <p>{message.text}</p>
        </div>
      ) : null}

      <div className="ownerPublicDemoSection" style={{ margin: '0 24px 16px', padding: '14px 16px', borderRadius: '8px', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', background: 'rgba(255,255,255,0.03)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-secondary, #94a3b8)', fontWeight: 700 }}>Explore Public</span>
          <h3 style={{ margin: '2px 0 0', fontSize: '14px', fontWeight: 600 }}>Integrations Demo (dirty)</h3>
          <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--text-secondary, #94a3b8)' }}>
            Canonical public demo with Google Drive, Notion, and Jira connections. Masking enabled.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Button
            size="sm"
            variant="secondary"
            icon={<RestoreRoundedIcon fontSize="inherit" />}
            disabled={resettingDemo}
            onClick={() => void handleResetDemo()}
          >
            {resettingDemo ? 'Resetting...' : 'Reset to canonical seed'}
          </Button>
        </div>
      </div>

      <div className="ownerTemplatesLayout">
        <nav className="ownerTemplatesRail" aria-label="Template categories and filters">
          <button className={category === 'all' ? 'isActive' : ''} type="button" onClick={() => setCategory('all')}>
            <Inventory2RoundedIcon fontSize="inherit" /><span>All templates</span><small>{rows.length}</small>
          </button>
          {MANAGED_CATEGORIES.map((item) => (
            <button className={category === item ? 'isActive' : ''} key={item} type="button" onClick={() => setCategory(item)}>
              {CATEGORY_ICONS[item]}<span>{item}</span><small>{countsByCategory.get(item) ?? 0}</small>
            </button>
          ))}
          <hr />
          <button className={filter === 'all' ? 'isActive' : ''} type="button" onClick={() => setFilter('all')}>
            <FilterAltRoundedIcon fontSize="inherit" /><span>All states</span><small>{rows.length}</small>
          </button>
          <button className={filter === 'drafts' ? 'isActive' : ''} type="button" onClick={() => setFilter('drafts')}>
            <UnpublishedRoundedIcon fontSize="inherit" /><span>Drafts</span><small>{statusCounts.drafts}</small>
          </button>
          <button className={filter === 'archived' ? 'isActive' : ''} type="button" onClick={() => setFilter('archived')}>
            <ArchiveRoundedIcon fontSize="inherit" /><span>Archived</span><small>{statusCounts.archived}</small>
          </button>
          <button className={filter === 'bundled-only' ? 'isActive' : ''} type="button" onClick={() => setFilter('bundled-only')}>
            <Inventory2RoundedIcon fontSize="inherit" /><span>Bundled-only</span><small>{statusCounts.bundledOnly}</small>
          </button>
          <button className={filter === 'firestore-only' ? 'isActive' : ''} type="button" onClick={() => setFilter('firestore-only')}>
            <FactCheckRoundedIcon fontSize="inherit" /><span>Firestore-only</span><small>{statusCounts.firestoreOnly}</small>
          </button>
          <hr />
          <button type="button" onClick={exportCurrentCategory}>
            <FileDownloadRoundedIcon fontSize="inherit" /><span>Export all</span><small>{exportRows.length}</small>
          </button>
        </nav>

        <main className="ownerTemplatesStage">
          {visibleRows.length === 0 ? (
            <div className="ownerEmptyState">
              <strong>No templates match this filter</strong>
              <p>Switch category or state filters to see the registry.</p>
            </div>
          ) : (
            <div className="ownerTemplatesGrid">
              {visibleRows.map((row) => (
                <button
                  className={`ownerTemplateCard${selectedId === row.id ? ' isSelected' : ''}`}
                  key={row.id}
                  type="button"
                  onClick={() => setSelectedId(row.id)}
                >
                  <span className={`ownerTemplateProvenance ownerTemplateProvenance--${row.provenance}`}>
                    {row.provenance === 'bundled' ? 'Bundled' : row.provenance === 'overriding' ? 'Overriding bundled' : `Firestore · ${row.firestore?.status ?? 'DRAFT'}`}
                  </span>
                  <span className="ownerTemplateCardIcon">{CATEGORY_ICONS[row.template.category]}</span>
                  <strong>{row.template.title}</strong>
                  <small>{row.template.category} · v{row.template.registryVersion}</small>
                  <p>{row.template.description}</p>
                  <span className="ownerTemplateTags">{row.template.tags.slice(0, 3).map((tag) => <i key={tag}>{tag}</i>)}</span>
                </button>
              ))}
            </div>
          )}
        </main>

        <aside className="ownerTemplatesSidecar" aria-label="Selected template admin preview">
          <PreviewRenderer template={selected?.template ?? null} />
          {selected ? (
            <div className="ownerTemplateMeta">
              <dl>
                <div><dt>ID</dt><dd>{selected.template.id}</dd></div>
                <div><dt>Status</dt><dd>{selected.firestore?.status ?? 'Bundled only'}</dd></div>
                <div><dt>Tier</dt><dd>{selected.template.tier ?? 'FREE'}</dd></div>
                <div><dt>Featured</dt><dd>{selected.template.featured ? 'Yes' : 'No'}</dd></div>
                <div><dt>Updated</dt><dd>{toDateLabel(selected.template.updatedAt)}</dd></div>
                <div><dt>Updated by</dt><dd>{selected.firestore?.updatedByUid || 'Bundled JSON'}</dd></div>
              </dl>
              <div className="ownerTemplateActions">
                <Button size="sm" variant="secondary" icon={<FileDownloadRoundedIcon fontSize="inherit" />} disabled={busy} onClick={() => exportTemplate(selected.template)}>Export</Button>
                {selectedDoc ? (
                  <>
                    <Button size="sm" variant="secondary" icon={<EditRoundedIcon fontSize="inherit" />} disabled={busy} onClick={() => openEditor(selectedDoc)}>Edit</Button>
                    <Button size="sm" variant="secondary" icon={<ContentCopyRoundedIcon fontSize="inherit" />} disabled={busy} onClick={() => {
                      const copy = asDoc({ ...selectedDoc, id: `${selectedDoc.id}-copy-${Date.now().toString(36)}`, title: `${selectedDoc.title} Copy`, registryVersion: 1 }, actorUid, 'DRAFT', null)
                      openEditor(copy)
                    }}>Duplicate</Button>
                    <Button size="sm" variant="secondary" icon={<CheckCircleRoundedIcon fontSize="inherit" />} disabled={busy || selectedDoc.status === 'PUBLISHED'} onClick={() => void run(() => updateOwnerExploreTemplateStatus(selectedDoc.id, 'PUBLISHED', actorUid), 'Template published.')}>Publish</Button>
                    <Button size="sm" variant="secondary" icon={<UnpublishedRoundedIcon fontSize="inherit" />} disabled={busy || selectedDoc.status === 'DRAFT'} onClick={() => void run(() => updateOwnerExploreTemplateStatus(selectedDoc.id, 'DRAFT', actorUid), 'Template moved to draft.')}>Unpublish</Button>
                    <Button size="sm" variant="secondary" icon={<ArchiveRoundedIcon fontSize="inherit" />} disabled={busy || selectedDoc.status === 'ARCHIVED'} onClick={() => void run(() => updateOwnerExploreTemplateStatus(selectedDoc.id, 'ARCHIVED', actorUid), 'Template archived.')}>Archive</Button>
                    <Button size="sm" variant="secondary" icon={<RestoreRoundedIcon fontSize="inherit" />} disabled={busy || selectedDoc.status !== 'ARCHIVED'} onClick={() => void run(() => updateOwnerExploreTemplateStatus(selectedDoc.id, 'DRAFT', actorUid), 'Template restored as draft.')}>Restore</Button>
                    {selected.bundled ? (
                      <Button size="sm" variant="tertiary" icon={<RestoreRoundedIcon fontSize="inherit" />} disabled={busy} onClick={() => {
                        if (window.confirm('Delete this Firestore override and restore the bundled template?')) void run(() => deleteOwnerExploreTemplate(selectedDoc.id), 'Bundled template restored.')
                      }}>Revert to bundled</Button>
                    ) : null}
                    <Button size="sm" variant="tertiary" icon={<DeleteRoundedIcon fontSize="inherit" />} disabled={busy} onClick={() => {
                      if (window.confirm('Hard-delete this Firestore template?')) void run(() => deleteOwnerExploreTemplate(selectedDoc.id), 'Template deleted.')
                    }}>Delete</Button>
                  </>
                ) : selected.bundled ? (
                  <>
                    <Button size="sm" variant="primary" icon={<EditRoundedIcon fontSize="inherit" />} disabled={busy} onClick={() => openEditor(asDoc(selected.bundled!, actorUid, 'DRAFT', selected.bundled!.id))}>Override in Firestore</Button>
                    <Button size="sm" variant="secondary" icon={<ContentCopyRoundedIcon fontSize="inherit" />} onClick={() => openEditor(asDoc({ ...selected.bundled!, id: `${selected.bundled!.id}-copy-${Date.now().toString(36)}`, title: `${selected.bundled!.title} Copy`, registryVersion: 1 }, actorUid, 'DRAFT', null))}>Duplicate</Button>
                  </>
                ) : null}
              </div>
              <p className="ownerTemplateCollectionHint">Collection: {EXPLORE_TEMPLATES_REGISTRY_COLLECTION}</p>
            </div>
          ) : null}
        </aside>
      </div>

      {editor ? (
        <div className="ownerTemplateEditorOverlay" role="dialog" aria-modal="true" aria-label="Edit Explore template">
          <div className="ownerTemplateEditor" ref={editorShellRef}>
            <header>
              <div>
                <span className="ownerEyebrow">{editor.category}</span>
                <h3>{editor.title || 'Untitled template'}</h3>
              </div>
              <div className="ownerSettingsActions">
                <Button variant="tertiary" size="sm" disabled={busy} onClick={() => setEditor(null)}>Cancel</Button>
                <Button variant="primary" size="sm" icon={<SaveRoundedIcon fontSize="inherit" />} loading={busy} onClick={() => void saveEditor()}>Save</Button>
              </div>
            </header>
            <div className="ownerTemplatePaneSwitch" role="tablist" aria-label="Template editor pane">
              <button className={editorPaneMode === 'fields' ? 'isActive' : ''} type="button" role="tab" aria-selected={editorPaneMode === 'fields'} onClick={() => setEditorPaneMode('fields')}>Fields</button>
              <button className={editorPaneMode === 'preview' ? 'isActive' : ''} type="button" role="tab" aria-selected={editorPaneMode === 'preview'} onClick={() => setEditorPaneMode('preview')}>Preview</button>
            </div>
            <div className="ownerTemplateEditorGrid">
              <section className={`ownerTemplateEditorFields ownerTemplateEditorPane${editorPaneMode === 'fields' ? ' isActive' : ''}`} aria-label="Template metadata">
                <label><span>ID</span><input value={editor.id} disabled={Boolean(firestoreTemplates.some((template) => template.id === editor.id))} onChange={(event) => setEditor({ ...editor, id: event.target.value })} /></label>
                <label><span>Title</span><input value={editor.title} onChange={(event) => setEditor({ ...editor, title: event.target.value, previewData: { ...editor.previewData, title: event.target.value } })} /></label>
                <label><span>Description</span><textarea value={editor.description} onChange={(event) => setEditor({ ...editor, description: event.target.value })} /></label>
                <label><span>Tags</span><input value={editor.tags.join(', ')} onChange={(event) => setEditor({ ...editor, tags: parseList(event.target.value) })} /></label>
                <label><span>Category</span><select value={editor.category} onChange={(event) => {
                  const nextCategory = event.target.value as TemplateCategory
                  setEditor({ ...editor, category: nextCategory })
                  setEditorContentMode(defaultEditorContentMode(nextCategory))
                }}>{MANAGED_CATEGORIES.map((item) => <option key={item}>{item}</option>)}</select></label>
                <label><span>Tier</span><select value={editor.tier ?? 'FREE'} onChange={(event) => setEditor({ ...editor, tier: event.target.value as TemplateTier })}><option>FREE</option><option>PREMIUM</option></select></label>
                <label><span>Status</span><select value={editor.status} onChange={(event) => setEditor({ ...editor, status: event.target.value as ExploreTemplateStatus })}><option>DRAFT</option><option>PUBLISHED</option><option>ARCHIVED</option></select></label>
                <label className="ownerTemplateToggleRow"><span>Featured</span><Toggle checked={editor.featured === true} onChange={(featured) => setEditor({ ...editor, featured })} /></label>
              </section>
              <section className={`ownerTemplateEditorFields ownerTemplateEditorFields--content ownerTemplateEditorPane${editorPaneMode === 'preview' ? ' isActive' : ''}`} aria-label="Template content">
                {editorContentMode === 'content' && editor.category === 'Checklists' && parsedEditorPayload ? (
                  <ChecklistPayloadEditor
                    value={normalizeChecklistPayload(parsedEditorPayload)}
                    onChange={updateChecklistPayload}
                  />
                ) : editorContentMode === 'content' && editor.category === 'Automations' && parsedEditorPayload ? (
                  <AutomationPayloadEditor
                    value={normalizeAutomationPayload(parsedEditorPayload)}
                    onChange={updateAutomationPayload}
                  />
                ) : editorContentMode === 'content' && editor.category === 'Forms' && parsedEditorPayload ? (
                  <FormPayloadEditor
                    value={normalizeFormPayload(parsedEditorPayload)}
                    onChange={updateFormPayload}
                  />
                ) : editorContentMode === 'content' && editor.category === 'Designs' && parsedEditorPayload ? (
                  <DesignPayloadEditor
                    value={normalizeDesignPayload(parsedEditorPayload)}
                    onChange={updateDesignPayload}
                  />
                ) : editorContentMode === 'rawPreview' ? (
                  <label className="ownerTemplateEditorJsonField">
                    <span>Preview data JSON</span>
                    <textarea value={editor.previewJson} onChange={(event) => setEditor({ ...editor, previewJson: event.target.value })} />
                  </label>
                ) : (
                  <label className="ownerTemplateEditorJsonField">
                    <span>{editor.category} payload JSON</span>
                    <textarea value={editor.payloadJson} onChange={(event) => setEditor({ ...editor, payloadJson: event.target.value })} />
                  </label>
                )}
              </section>
            </div>
            <footer className="ownerTemplateEditorFooter">
              <div className="ownerTemplateEditorTabs" role="tablist" aria-label="Template content mode">
                <button className={editorContentMode === 'content' ? 'isActive' : ''} type="button" role="tab" aria-selected={editorContentMode === 'content'} onClick={() => setEditorContentMode('content')}>Content</button>
                <button className={editorContentMode === 'rawPreview' ? 'isActive' : ''} type="button" role="tab" aria-selected={editorContentMode === 'rawPreview'} onClick={() => setEditorContentMode('rawPreview')}>Raw preview</button>
                <button className={editorContentMode === 'rawPayload' ? 'isActive' : ''} type="button" role="tab" aria-selected={editorContentMode === 'rawPayload'} onClick={() => setEditorContentMode('rawPayload')}>Raw payload</button>
              </div>
              <span>{editor.category} · {busy ? 'Saving' : 'Unsaved editor draft'}</span>
            </footer>
          </div>
        </div>
      ) : null}

      {importOpen ? (
        <div className="ownerTemplateEditorOverlay" role="dialog" aria-modal="true" aria-label="Import Explore templates">
          <div className="ownerTemplateImportDialog">
            <header>
              <div>
                <span className="ownerEyebrow">Import JSON</span>
                <h3>Preview registry changes</h3>
              </div>
              <div className="ownerSettingsActions">
                <Button variant="tertiary" size="sm" disabled={busy} onClick={() => { setImportOpen(false); setImportPreview(null) }}>Cancel</Button>
                <Button variant="primary" size="sm" loading={busy} disabled={!importPreview} onClick={() => void confirmImport()}>Confirm import</Button>
              </div>
            </header>
            <label className="ownerTemplateEditorJsonField">
              <span>TemplateRegistryEntry[] JSON</span>
              <textarea value={importJson} onChange={(event) => { setImportJson(event.target.value); setImportPreview(null) }} />
            </label>
            <div className="ownerTemplateImportActions">
              <input
                aria-label="Upload template JSON"
                type="file"
                accept="application/json,.json"
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  if (!file) return
                  void file.text().then((text) => { setImportJson(text); setImportPreview(null) })
                }}
              />
              <Button variant="secondary" size="sm" onClick={previewImport}>Preview import</Button>
            </div>
            {importPreview ? (
              <div className="ownerTemplateImportPreview" role="status">
                <strong>{importPreview.entries.length} entries parsed</strong>
                <span>Adds {importPreview.adds}</span>
                <span>Overrides {importPreview.overrides}</span>
                <span>No-op {importPreview.noops}</span>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  )
}
